import { NgModule } from '@angular/core';
import { Routes, RouterModule, UrlSegment } from '@angular/router';
import { QuicklinkModule, QuicklinkStrategy } from 'ngx-quicklink';

import { environment } from '../environments/environment';

// Matcher functions for special routes
export function matchProducts(url: UrlSegment[]) {
    const isMatch = url.length == 1 && new RegExp(environment.productUrlRegex).test(url[0].path);
    return isMatch ? { consumed: url, posParams: { productUrl: url[0] } } : null;
}

export const matchServiceCategory = (url: UrlSegment[]) => {
    if (url.length > 1 && url[0].path === 'Services') {
        // Pass the service category as posParams so child routes can access it via route.params
        // url[1] contains the service category name (e.g., 'SmartHands-Services')
        return { consumed: url, posParams: { serviceCategory: url[1] } };
    }
    return null;
};

export function matchCmsGatedContent(url: UrlSegment[]) {
    const paths = ['eBooks', 'Whitepapers'];
    if (url.length >= 1 && url.length <= 2 && paths.find((path: string) => path === url[0].path)) {
        // Only consume the first segment (content type). Leave the slug (if any) for child routes.
        return { consumed: [url[0]], posParams: { contentType: url[0] } };
    }
    return null;
}

const routes: Routes = [
    // Homepage - lazy loaded
    {
        path: '',
        loadChildren: () => import('./cms/pages/cms-homepage/cms-homepage.module').then((m) => m.HomepageModule),
    },
    // Blog - lazy loaded
    {
        path: 'blog',
        loadChildren: () => import('./cms/blog/blog.module').then((m) => m.BlogModule),
    },
    // Category - lazy loaded
    {
        path: 'category',
        loadChildren: () => import('./category/category.module').then((m) => m.CategoryModule),
    },
    // Configurator - lazy loaded
    {
        path: ':product/configurator',
        loadChildren: () => import('./configurator/configurator.module').then((m) => m.ConfiguratorModule),
    },
    // About Us - lazy loaded
    {
        path: 'About-Us',
        loadChildren: () => import('./cms/pages/cms-about-us/cms-about-us.module').then((m) => m.AboutUsModule),
    },
    // Account Application - lazy loaded
    {
        path: 'Account-Application',
        loadChildren: () =>
            import('./cms/pages/cms-account-application/cms-account-application.module').then((m) => m.AccountApplicationModule),
    },
    // B2B Services - lazy loaded
    {
        path: 'B2B-Services',
        loadChildren: () => import('./cms/pages/cms-b2b-service/cms-b2b-service.module').then((m) => m.B2bServiceModule),
    },
    // Careers - lazy loaded
    {
        path: 'Careers',
        loadChildren: () => import('./cms/pages/cms-careers/cms-careers.module').then((m) => m.CareersModule),
    },
    // Contact Us - lazy loaded
    {
        path: 'Contact-Us',
        loadChildren: () => import('./cms/pages/cms-contact-us/cms-contact-us.module').then((m) => m.ContactUsModule),
    },
    // Customers - lazy loaded
    {
        path: 'Customers',
        loadChildren: () => import('./cms/pages/cms-customers/cms-customers.module').then((m) => m.CustomersModule),
    },
    // Documents - lazy loaded
    {
        path: 'documents',
        loadChildren: () => import('./cms/pages/cms-document-listing/cms-document.module').then((m) => m.DocumentModule),
    },
    // Event pages - lazy loaded
    {
        path: 'Event',
        loadChildren: () => import('./cms/pages/cms-event-form-page/cms-event-form-page.module').then((m) => m.EventFormPageModule),
    },
    // News & Events - lazy loaded
    {
        path: 'News-Events',
        loadChildren: () => import('./cms/pages/cms-news-events/cms-news-events.module').then((m) => m.NewsEventsModule),
    },
    // DGX Store - lazy loaded (part of product module)
    {
        path: 'DGX-Store',
        loadChildren: () => import('./product/product.module').then((m) => m.ProductModule),
    },
    // Partners - lazy loaded
    {
        path: 'Partners',
        loadChildren: () => import('./cms/pages/cms-partners/cms-partners.module').then((m) => m.PartnersModule),
    },
    // Resource Center - lazy loaded
    {
        path: 'Resource-Center',
        loadChildren: () => import('./cms/pages/cms-resource-center/cms-resource-center.module').then((m) => m.ResourceCenterModule),
    },
    // Asset Repository - lazy loaded
    {
        path: 'Asset-Repository',
        loadChildren: () => import('./cms/pages/cms-repository/cms-repository.module').then((m) => m.RepositoryModule),
    },
    // RMA Request - lazy loaded
    {
        path: 'RMA-Request',
        loadChildren: () => import('./cms/pages/cms-rma-request/cms-rma-request.module').then((m) => m.RmaRequestModule),
    },
    // Supported Software - lazy loaded
    {
        path: 'Supported-Software',
        loadChildren: () =>
            import('./cms/pages/cms-supported-software/cms-supported-software.module').then((m) => m.SupportedSoftwareModule),
    },
    // Test Drive - lazy loaded
    {
        path: 'Test-Drive',
        loadChildren: () => import('./cms/pages/cms-test-drive/cms-test-drive.module').then((m) => m.TestDriveModule),
    },
    // Privacy - lazy loaded
    {
        path: 'Privacy',
        loadChildren: () => import('./privacy/privacy.module').then((m) => m.PrivacyModule),
    },
    // Cookies - lazy loaded
    {
        path: 'Cookies',
        loadChildren: () => import('./privacy/privacy.module').then((m) => m.PrivacyModule),
    },
    // Case Studies - lazy loaded
    {
        path: 'case-studies',
        loadChildren: () => import('./cms/pages/cms-case-studies/cms-case-studies.module').then((m) => m.CaseStudiesModule),
    },
    // Reference Architecture - lazy loaded
    {
        path: 'Reference-Architecture',
        loadChildren: () =>
            import('./cms/pages/cms-reference-architecture/cms-reference-architecture.module').then((m) => m.ReferenceArchitectureModule),
    },
    // Services category matcher - lazy loaded. `categoryPages` tells CategoryService.initRoutes that
    // category_page entries under /Services/* belong here and are not shadowing an app route [ST-3626].
    {
        matcher: matchServiceCategory,
        data: { categoryPages: true },
        loadChildren: () => import('./category/category.module').then((m) => m.CategoryModule),
    },
    // Gated content - lazy loaded
    {
        matcher: matchCmsGatedContent,
        loadChildren: () => import('./cms/pages/cms-gated-content/cms-gated-content.module').then((m) => m.GatedContentModule),
    },
    // Product pages - lazy loaded (must be near the end due to matcher)
    {
        matcher: matchProducts,
        loadChildren: () => import('./product/product.module').then((m) => m.ProductModule),
    },
    // 410 - offboarded product/category, lazy loaded
    {
        path: '410',
        loadChildren: () => import('./offboarded/offboarded.module').then((m) => m.OffboardedModule),
    },
    // 404 - lazy loaded
    {
        path: '404',
        loadChildren: () => import('./not-found/not-found.module').then((m) => m.NotFoundModule),
    },
    // Catch-all - must be last
    {
        path: '**',
        loadChildren: () => import('./not-found/not-found.module').then((m) => m.NotFoundModule),
    },
];

@NgModule({
    imports: [
        RouterModule.forRoot(routes, {
            initialNavigation: 'enabledNonBlocking',
            scrollPositionRestoration: 'disabled',
            preloadingStrategy: QuicklinkStrategy,
        }),
        QuicklinkModule,
    ],
    exports: [RouterModule, QuicklinkModule],
})
export class AppRoutingModule {}
